import { useEffect, useState } from 'react';
import {
    HubConnectionBuilder,
    HubConnectionState,
    ILogger,
    LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '../auth/useAuth';
import { ExactLimitsBoard } from '../api/exactLimitsBoard';
import { rolloutHubUrl } from '../api/rollout';

const quietLogger: ILogger = {
    log(logLevel, message) {
        if (
            logLevel >= LogLevel.Warning &&
            !message.includes('stopped during negotiation')
        ) {
            console.warn(`[limits-hub] ${message}`);
        }
    },
};

/**
 * Exact's limits of one environment, pushed by the server as they move: the board every caller
 * with the connector's token keeps up to date. No polling from the page.
 */
export const useLiveExactLimits = (customer?: string, environment?: string) => {
    const { getAccessTokenSilently } = useAuth();
    const [board, setBoard] = useState<ExactLimitsBoard>();

    useEffect(() => {
        setBoard(undefined);

        if (!customer || !environment) return;

        let cancelled = false;

        const hub = new HubConnectionBuilder()
            .withUrl(rolloutHubUrl, {
                accessTokenFactory: () => getAccessTokenSilently(),
            })
            .withAutomaticReconnect()
            .configureLogging(quietLogger)
            .build();

        const follow = async () => {
            const first = await hub.invoke<ExactLimitsBoard>(
                'followLimits',
                customer,
                environment
            );

            if (!cancelled && first && !first.error) setBoard(first);
        };

        hub.on(
            'exactLimits',
            (message: {
                customer: string;
                environment: string;
                board: ExactLimitsBoard;
            }) => {
                if (
                    cancelled ||
                    message.customer !== customer ||
                    message.environment !== environment
                )
                    return;

                if (!message.board.error) setBoard(message.board);
            }
        );
        hub.onreconnected(() => !cancelled && follow().catch(() => undefined));

        hub.start()
            .then(() => (cancelled ? undefined : follow()))
            .catch(() => undefined);

        return () => {
            cancelled = true;

            if (hub.state !== HubConnectionState.Disconnected) {
                hub.stop().catch(() => undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, environment]);

    return board;
};
